import React, { useContext, useEffect, useState } from 'react';
import {
  Link as RouterLink,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar as MuiAppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  Breadcrumbs,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LogoutIcon from '@mui/icons-material/Logout';
import LanguageIcon from '@mui/icons-material/Language';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useAuth } from '../contexts/AuthContext';
import { signOut } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import {
  ConfirmationNumberOutlined,
  Diversity1Outlined,
  MapOutlined,
  MenuBookOutlined,
  PeopleOutlineOutlined,
  WorkOutlineOutlined,
  TaskAltOutlined,
} from '@mui/icons-material';
import AppBarComponent from './AppBar';
import { useSelector, useDispatch } from 'react-redux';
import { setActiveModule, clearSearch } from '../redux/slices/searchSlice';
import { useTranslation } from 'react-i18next';
import MenuIcon from '@mui/icons-material/Menu';
import UserProfileMenu from './UserProfileMenu';

const drawerWidth = 320;

const menuItems = [
  {
    text: 'menu.dashboard',
    path: '/dashboard',
    icon: <DashboardIcon />,
  },
  {
    text: 'menu.accounts',
    path: '/customers',
    icon: <PeopleOutlineOutlined />,
  },
  { text: 'menu.resellers', path: '/resellers', icon: <Diversity1Outlined /> },
  {
    text: 'menu.tickets',
    path: '/tickets',
    icon: <ConfirmationNumberOutlined />,
  },
  { text: 'menu.projects', path: '/projects', icon: <WorkOutlineOutlined /> },
  {
    text: 'menu.tasks',
    path: '/tasks',
    icon: <TaskAltOutlined />,
  },
  {
    text: 'menu.knowledgeBase',
    path: '/knowledge-base',
    icon: <MenuBookOutlined />,
  },
];

function Layout() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const dispatch = useDispatch();
  const { query } = useSelector((state) => state.search);
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [language, setLanguage] = useState(i18n.language);
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [openProjectDialog, setOpenProjectDialog] = useState(false);
  const [openTicketDialog, setOpenTicketDialog] = useState(false);
  const [openKnowledgeBaseDialog, setOpenKnowledgeBaseDialog] = useState(false);
  const [openTaskDialog, setOpenTaskDialog] = useState(false);

  useEffect(() => {
    // Clear search when changing pages
    dispatch(clearSearch());
  }, [location.pathname, dispatch]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    dispatch({ type: 'search/setSearchQuery', payload: value });
  };

  const handleModuleChange = (path) => {
    const module = path.split('/')[1];
    dispatch(setActiveModule(module));
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    setLanguage(newLanguage);
    i18n.changeLanguage(newLanguage);
  };

  const handleCreateCustomer = () => {
    setOpenCustomerDialog(true);
  };

  const handleCreateProject = () => {
    setOpenProjectDialog(true);
  };

  const handleCreateTicket = () => {
    setOpenTicketDialog(true);
  };

  const handleCreateKnowledgeBase = () => {
    setOpenKnowledgeBaseDialog(true);
  };

  const handleCreateTask = () => {
    setOpenTaskDialog(true);
  };

  const drawer = (
    <div>
      <Toolbar />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              selected={location.pathname === item.path}
              onClick={() => handleModuleChange(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={t(item.text)} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  // Define common glass styles to reuse
  const glassBorderStyle = '1px solid rgba(255, 255, 255, 0.3)';
  const glassBgAppBarDrawer = 'rgba(255, 255, 255, 0.75)';
  const glassBgPaper = theme.palette.background.paper;
  const glassBackdropFilterAppBarDrawer = 'blur(12px) saturate(180%)';
  const glassBackdropFilterPaper = 'blur(10px) saturate(160%)';

  const getPageTitle = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    switch (pathSegments[0]) {
      case 'customers':
        return 'Accounts';
      case 'tickets':
        return 'Tickets';
        return 'BluePrint';
      case 'knowledge-base':
        return 'Knowledge Base';
      case 'projects':
        return 'Projects';
      default:
        return 'Dashboard';
    }
  };

  return (
    <Box sx={{ display: 'flex', paddingRight: '20px' }}>
      <MuiAppBar
        position="fixed"
        elevation={0}
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          background: 'transparent',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          boxShadow: 'none',
          border: 'none',
          color: theme.palette.text.primary,
        }}
      >
        <Box
          sx={{
            m: 2.5,
            borderRadius: theme.shape.borderRadius * 1.5,
            background: glassBgAppBarDrawer,
            backdropFilter: glassBackdropFilterAppBarDrawer,
            WebkitBackdropFilter: glassBackdropFilterAppBarDrawer,
            border: glassBorderStyle,
            overflow: 'hidden',
          }}
        >
          <AppBarComponent
            pathPosition={
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="text.primary"
                  fontSize="1.2rem"
                  fontWeight="bold"
                >
                  {getPageTitle()}
                </Typography>
              </Breadcrumbs>
            }
            searchComponent={
              <TextField
                fullWidth
                size="small"
                placeholder={`${t('search.placeholder')} ${getPageTitle()}...`}
                value={query}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            }
            onCreateCustomer={
              location.pathname.startsWith('/customers')
                ? handleCreateCustomer
                : null
            }
            onCreateProject={
              location.pathname.startsWith('/projects')
                ? handleCreateProject
                : null
            }
            onCreateTicket={
              location.pathname.startsWith('/tickets')
                ? handleCreateTicket
                : null
            }
            onCreateKnowledgeBase={
              location.pathname.startsWith('/knowledge-base')
                ? handleCreateKnowledgeBase
                : null
            }
            onCreateTask={
              location.pathname.startsWith('/tasks') ? handleCreateTask : null
            }
          />
        </Box>
      </MuiAppBar>

      <Drawer
        variant="permanent"
        anchor="left"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: `calc(${drawerWidth}px - 40px)`,
            height: 'calc(100% - 40px)',
            boxSizing: 'border-box',
            m: 2.5,
            borderRadius: theme.shape.borderRadius * 1.5,
            border: glassBorderStyle,
            background: glassBgAppBarDrawer,
            backdropFilter: glassBackdropFilterAppBarDrawer,
            WebkitBackdropFilter: glassBackdropFilterAppBarDrawer,
            boxShadow: theme.shadows[1],
            color: theme.palette.text.primary,
            overflow: 'hidden',
          },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          marginTop: '100px',
          marginLeft: '20px',
          p: 2.5,
          height: '85vh',
          borderRadius: theme.shape.borderRadius * 1.5,
          border: glassBorderStyle,
          background: glassBgPaper,
          backdropFilter: glassBackdropFilterPaper,
          WebkitBackdropFilter: glassBackdropFilterPaper,
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(0, 0, 0, 0.3)',
          },
        }}
      >
        <Outlet
          context={{
            openCustomerDialog,
            setOpenCustomerDialog,
            openProjectDialog,
            setOpenProjectDialog,
            openTicketDialog,
            setOpenTicketDialog,
            openKnowledgeBaseDialog,
            setOpenKnowledgeBaseDialog,
            openTaskDialog,
            setOpenTaskDialog,
          }}
        />
      </Box>
    </Box>
  );
}

export default Layout;
